import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileText, CalendarDays, Calendar, CalendarCheck, UserCircle, TrendingUp, Heart, Layers, Target, Star } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

import MesBulletins from "./MesBulletins";
import MesConges from "./MesConges";
import MonPlanningPerso from "./MonPlanningPerso";
import MesJoursFeries from "./MesJoursFeries";
import MonProfil from "./MonProfil";
import MesSoldes from "./MesSoldes";
import MonBienEtre from "./MonBienEtre";
import MesCompetences from "./MesCompetences";
import MonIDP from "./MonIDP";
import MaReconnaissance from "./MaReconnaissance";

function cn(...inputs) {
  return inputs.filter(Boolean).join(" ");
}

const MonEspace = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "profil";
  const setActiveTab = (tab) => setSearchParams({ tab }, { replace: true });

  const tabs = [
    {
      id: "profil",
      label: t("portal.mySpace.tabs.profile"),
      icon: UserCircle,
      description: t("portal.mySpace.tabs.profileDesc"),
      badge: null,
    },
    {
      id: "bulletins",
      label: t("portal.mySpace.tabs.payslips"),
      icon: FileText,
      description: t("portal.mySpace.tabs.payslipsDesc"),
      badge: null,
    },
    {
      id: "conges",
      label: t("portal.mySpace.tabs.leaves"),
      icon: CalendarDays,
      description: t("portal.mySpace.tabs.leavesDesc"),
      badge: null,
    },
    {
      id: "planning",
      label: t("portal.mySpace.tabs.schedule"),
      icon: Calendar,
      description: t("portal.mySpace.tabs.scheduleDesc"),
      badge: null,
    },
    {
      id: "feries",
      label: t("portal.mySpace.tabs.holidays"),
      icon: CalendarCheck,
      description: t("portal.mySpace.tabs.holidaysDesc"),
      badge: null,
    },
    {
      id: "soldes",
      label: t("portal.mySpace.tabs.balances"),
      icon: TrendingUp,
      description: t("portal.mySpace.tabs.balancesDesc"),
      badge: null,
    },
    {
      id: "bienetre",
      label: t("portal.mySpace.tabs.wellbeing"),
      icon: Heart,
      description: t("portal.mySpace.tabs.wellbeingDesc"),
      badge: null,
    },
    {
      id: "competences",
      label: t("portal.mySpace.tabs.skills"),
      icon: Layers,
      description: t("portal.mySpace.tabs.skillsDesc"),
      badge: null,
    },
    {
      id: "idp",
      label: t("portal.mySpace.tabs.idp"),
      icon: Target,
      description: t("portal.mySpace.tabs.idpDesc"),
      badge: null,
    },
    {
      id: "reconnaissance",
      label: t("portal.mySpace.tabs.recognition"),
      icon: Star,
      description: t("portal.mySpace.tabs.recognitionDesc"),
      badge: null,
    },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case "profil":    return <MonProfil />;
      case "bulletins": return <MesBulletins />;
      case "conges":    return <MesConges />;
      case "planning":  return <MonPlanningPerso />;
      case "feries":         return <MesJoursFeries />;
      case "soldes":         return <MesSoldes />;
      case "bienetre":       return <MonBienEtre />;
      case "competences":    return <MesCompetences />;
      case "idp":            return <MonIDP />;
      case "reconnaissance": return <MaReconnaissance />;
      default:               return null;
    }
  };

  const current = tabs.find((t) => t.id === activeTab);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t("portal.mySpace.title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {t("portal.mySpace.subtitle")}
        </p>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-2 flex-wrap">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all",
                isActive
                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                  : "bg-transparent text-muted-foreground border-border hover:bg-accent hover:text-foreground"
              )}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              {tab.badge && (
                <Badge variant="secondary" className="text-xs px-1.5 py-0">
                  {tab.badge}
                </Badge>
              )}
            </button>
          );
        })}
      </div>

      {/* Current tab description */}
      {current && (
        <p className="text-sm text-muted-foreground -mt-2">{current.description}</p>
      )}

      {/* Tab content */}
      <div>{renderContent()}</div>
    </div>
  );
};

export default MonEspace;
